/**
 * Shared logic for the sign-off added beneath a clinic letter.
 *
 * A sign-off is stored with a token where the handwritten signature goes, never the image itself:
 * the server puts the author's signature in when it makes the PDF. Editors show the token as the
 * image (or a placeholder when there is none) and turn it back into the token on the way out.
 */
app.service("SignOffService", [
	function () {
		const SIGNATURE_IMAGE_TOKEN = "${SIGNATURE_IMAGE}";
		const SIGNATURE_IMAGE_ATTRIBUTE = "data-sign-off-signature";

		// The size the editor shows text at when nothing sets one. The PDF renderer's is 16px.
		const BASE_FONT_SIZE = "12px";

		// The blocks the server keeps. Text inside them is sized; the blocks themselves are left alone.
		const BLOCK_TAGS = ["P", "DIV", "UL", "OL", "LI", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"];

		function escapeHtml(text) {
			return String(text || "")
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;")
				.replace(/"/g, "&quot;");
		}

		/**
		 * A grey box with a label, shown where the signature goes when there is no image to show -
		 * the user has not made one yet, or it belongs to someone else.
		 */
		function placeholderImage(label) {
			const svg =
				'<svg xmlns="http://www.w3.org/2000/svg" width="600" height="200">' +
				'<rect x="2" y="2" width="596" height="196" rx="12" fill="#f4f6fb" stroke="#9aa5c4" stroke-width="4" stroke-dasharray="14 10"/>' +
				'<text x="300" y="114" font-family="Arial, sans-serif" font-size="36" fill="#6b7591" text-anchor="middle">' +
				escapeHtml(label || "Signature") +
				"</text></svg>";
			return "data:image/svg+xml;base64," + window.btoa(unescape(encodeURIComponent(svg)));
		}

		function signatureImageHtml(signatureDataUrl, placeholderLabel) {
			const src = signatureDataUrl || placeholderImage(placeholderLabel);
			return `<img ${SIGNATURE_IMAGE_ATTRIBUTE}="true" src="${src}" alt="Signature" style="height:50px;">`;
		}

		function hasFontSize(element, root) {
			for (let el = element; el && el !== root; el = el.parentElement) {
				if (el.style && el.style.fontSize) {
					return true;
				}
			}
			return false;
		}

		/** Whether a child of a block takes the page's size, and so needs a sized span around it. */
		function needsSizedSpan(child) {
			if (child.nodeType === Node.TEXT_NODE) {
				return child.textContent.trim() !== "";
			}
			return (
				child.nodeType === Node.ELEMENT_NODE &&
				child.tagName !== "SPAN" &&
				!BLOCK_TAGS.includes(child.tagName) &&
				!child.style.fontSize
			);
		}

		/**
		 * Gives text an explicit size wherever it would otherwise take the page's. The editor shows that
		 * text at 12px but the PDF renderer's base is 16px, so it printed larger than it was written -
		 * after "clear formatting", retyping a cleared sign-off, or pasting. The letter text gets the same
		 * treatment (sanitizer in clinicLetterController), except that here a span inside sized text,
		 * such as a highlight, keeps that size rather than going back to 12px.
		 */
		function sizeUnsizedText(doc) {
			doc.querySelectorAll("span").forEach((span) => {
				if (!hasFontSize(span, doc.body)) {
					span.style.fontSize = BASE_FONT_SIZE;
				}
			});
			// Bold, italic or plain text straight inside a block goes in a sized span. Headings keep their size.
			[doc.body, ...doc.body.querySelectorAll("p, div, li, blockquote")].forEach((block) => {
				if (hasFontSize(block, doc.body)) {
					return;
				}
				Array.from(block.childNodes).filter(needsSizedSpan).forEach((child) => {
					const span = doc.createElement("span");
					span.style.fontSize = BASE_FONT_SIZE;
					child.replaceWith(span);
					span.appendChild(child);
				});
			});
		}

		const service = {
			SIGNATURE_IMAGE_TOKEN,

			/** Sign-off HTML as stored -> HTML for an editor or preview, with the signature showing. */
			toDisplayHtml: function (html, signatureDataUrl, placeholderLabel) {
				return (html || "").split(SIGNATURE_IMAGE_TOKEN).join(signatureImageHtml(signatureDataUrl, placeholderLabel));
			},

			/**
			 * Editor HTML -> sign-off HTML to store. The signature image goes back to the token. Any
			 * other image is dropped: the server refuses them, and a pasted image would only bloat the
			 * letter.
			 *
			 * The editor's colour button writes <font color>, whose attributes the server strips, so it
			 * becomes a styled span the way the letter text's does. Text with no size of its own is
			 * given the editor's, so the PDF prints it at the size it was written.
			 */
			toStoredHtml: function (html) {
				const doc = new DOMParser().parseFromString(html || "", "text/html");
				doc.querySelectorAll("img").forEach((img) => {
					if (img.hasAttribute(SIGNATURE_IMAGE_ATTRIBUTE)) {
						img.replaceWith(doc.createTextNode(SIGNATURE_IMAGE_TOKEN));
					} else {
						img.remove();
					}
				});
				doc.querySelectorAll("font").forEach((font) => {
					const span = doc.createElement("span");
					if (font.getAttribute("color")) {
						span.style.color = font.getAttribute("color");
					}
					if (font.getAttribute("face")) {
						span.style.fontFamily = font.getAttribute("face");
					}
					while (font.firstChild) {
						span.appendChild(font.firstChild);
					}
					font.replaceWith(span);
				});
				sizeUnsizedText(doc);
				return doc.body.innerHTML;
			},

			/** The image node the editor's Signature button inserts. */
			createSignatureImage: function (signatureDataUrl, placeholderLabel) {
				const template = document.createElement("template");
				template.innerHTML = signatureImageHtml(signatureDataUrl, placeholderLabel);
				return template.content.firstChild;
			},

			/** Whether a sign-off has anything in it worth adding to a letter. */
			hasContent: function (html) {
				if (!html) {
					return false;
				}
				if (html.indexOf(SIGNATURE_IMAGE_TOKEN) !== -1) {
					return true;
				}
				const doc = new DOMParser().parseFromString(html, "text/html");
				return doc.body.textContent.replace(/﻿/g, "").trim().length > 0;
			},

			/** A first sign-off for a user who has not saved one, built from their name. */
			starterHtml: function (user) {
				const name = [user && user.title, user && user.firstName, user && user.lastName].filter(Boolean).join(" ");
				return (
					'<p><span style="font-size:12px;">Kind regards,</span></p>' +
					`<p>${SIGNATURE_IMAGE_TOKEN}</p>` +
					`<p><span style="font-size:12px;"><b>${escapeHtml(name)}</b></span></p>`
				);
			},

			/** A canvas data URL -> the PNG blob the signature upload expects. */
			dataUrlToBlob: function (dataUrl) {
				const parts = dataUrl.split(";base64,");
				const contentType = parts[0].split(":")[1];
				const raw = window.atob(parts[1]);
				const bytes = new Uint8Array(raw.length);
				for (let index = 0; index < raw.length; index++) {
					bytes[index] = raw.charCodeAt(index);
				}
				return new Blob([bytes], { type: contentType });
			},
		};

		return service;
	},
]);
